// src/components/Events/Events/EventSpaces/EventSpaces.js - Fixed response handling
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { axiosInstance } from "../../../../utils/axios";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  FormGroup,
  Paper,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChatBubble as ChatIcon,
  LiveTv as LiveTvIcon,
  TableBar as TableIcon,
} from "@mui/icons-material";

const BoothCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[8],
  },
}));

const FeatureChip = styled(Chip)(({ theme, active }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
}));

// Form validation schema
const boothValidationSchema = Yup.object({
  boothName: Yup.string()
    .required("Booth name is required")
    .max(100, "Booth name cannot exceed 100 characters"),
  boothExhibitors: Yup.string().max(
    500,
    "Exhibitor information cannot exceed 500 characters"
  ),
  boothTags: Yup.string().max(500, "Tags cannot exceed 500 characters"),
  numberOfTables: Yup.number()
    .min(0, "Number of tables cannot be negative")
    .max(20, "Maximum 20 tables allowed")
    .when("tablesEnabled", {
      is: true,
      then: (schema) =>
        schema.required("Number of tables is required when tables are enabled"),
    }),
  description: Yup.string().max(
    2000,
    "Description cannot exceed 2000 characters"
  ),
  websiteUrl: Yup.string()
    .url("Must be a valid URL")
    .max(500, "URL cannot exceed 500 characters"),
});

function EventSpacesComponent({ eventId }) {
  const [activeTab, setActiveTab] = useState("Booths");
  const [booths, setBooths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooth, setEditingBooth] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [boothToDelete, setBoothToDelete] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Initial form values for creating/editing a booth
  const initialValues = {
    boothName: "",
    boothExhibitors: "",
    boothTags: "",
    chatEnabled: true,
    tablesEnabled: false,
    liveBroadcastEnabled: false,
    numberOfTables: 0,
    description: "",
    logoUrl: "",
    websiteUrl: "",
  };

  // Fetch booths when component mounts
  useEffect(() => {
    if (eventId) {
      fetchBooths();
    }
  }, [eventId]);

  const fetchBooths = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetBooths($eventId: ID!) {
            getBooths(eventId: $eventId) {
              id
              boothName
              boothExhibitors
              boothTags
              chatEnabled
              tablesEnabled
              liveBroadcastEnabled
              numberOfTables
              description
              logoUrl
              websiteUrl
              createdAt
              updatedAt
            }
          }
        `,
        variables: {
          eventId,
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0]?.message || "Error fetching booths");
      }

      const fetchedBooths = response.data?.getBooths || [];
      setBooths(fetchedBooths);
    } catch (error) {
      console.error("Error fetching booths:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to load booths",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (booth = null) => {
    if (booth) {
      setEditingBooth(booth);
    } else {
      setEditingBooth(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBooth(null);
  };

  // Parse tags string into array for backend
  const parseTagsString = (tagsString) => {
    if (!tagsString) return [];

    // Split by commas and trim whitespace
    return tagsString
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Convert tags string to array for the backend
      const parsedTags = parseTagsString(values.boothTags);

      if (editingBooth) {
        // Update existing booth
        const response = await axiosInstance.post("/graphql", {
          query: `
            mutation UpdateBooth($id: ID!, $eventId: ID!, $input: UpdateBoothInput!) {
              updateBooth(id: $id, eventId: $eventId, input: $input) {
                id
                boothName
                boothExhibitors
                boothTags
                chatEnabled
                tablesEnabled
                liveBroadcastEnabled
                numberOfTables
                description
                logoUrl
                websiteUrl
              }
            }
          `,
          variables: {
            id: editingBooth.id,
            eventId,
            input: {
              boothName: values.boothName,
              boothExhibitors: values.boothExhibitors,
              boothTags: values.boothTags,
              chatEnabled: values.chatEnabled,
              tablesEnabled: values.tablesEnabled,
              liveBroadcastEnabled: values.liveBroadcastEnabled,
              numberOfTables: values.numberOfTables,
              description: values.description,
              logoUrl: values.logoUrl,
              websiteUrl: values.websiteUrl,
            },
          },
        });

        if (response.errors) {
          throw new Error(
            response.errors[0]?.message || "Error updating booth"
          );
        }

        // Update local state
        setBooths(
          booths.map((booth) =>
            booth.id === editingBooth.id ? response.data.updateBooth : booth
          )
        );

        setAlert({
          open: true,
          message: "Booth updated successfully!",
          severity: "success",
        });
      } else {
        // Create new booth
        const response = await axiosInstance.post("/graphql", {
          query: `
            mutation CreateBooth($input: CreateBoothInput!) {
              createBooth(input: $input) {
                id
                boothName
                boothExhibitors
                boothTags
                chatEnabled
                tablesEnabled
                liveBroadcastEnabled
                numberOfTables
                description
                logoUrl
                websiteUrl
                createdAt
                updatedAt
              }
            }
          `,
          variables: {
            input: {
              eventId,
              ...values,
              boothTags: values.boothTags, // Keep as string in this case
            },
          },
        });

        if (response.errors) {
          throw new Error(
            response.errors[0]?.message || "Error creating booth"
          );
        }

        // Add new booth to state
        setBooths([...booths, response.data.createBooth]);

        setAlert({
          open: true,
          message: "Booth created successfully!",
          severity: "success",
        });
      }

      resetForm();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving booth:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to save booth",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (booth) => {
    setBoothToDelete(booth);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation DeleteBooth($id: ID!, $eventId: ID!) {
            deleteBooth(id: $id, eventId: $eventId)
          }
        `,
        variables: {
          id: boothToDelete.id,
          eventId,
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0]?.message || "Error deleting booth");
      }

      // Remove booth from state
      setBooths(booths.filter((booth) => booth.id !== boothToDelete.id));

      setAlert({
        open: true,
        message: "Booth deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting booth:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to delete booth",
        severity: "error",
      });
    } finally {
      setDeleteConfirmOpen(false);
      setBoothToDelete(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Paper sx={{ height: "100%" }}>
          <List component="nav">
            <ListItem
              button
              onClick={() => handleTabChange("Booths")}
              selected={activeTab === "Booths"}
              sx={{
                borderLeft: "3px solid",
                borderLeftColor:
                  activeTab === "Booths" ? "primary.main" : "transparent",
                backgroundColor:
                  activeTab === "Booths"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                minHeight: "50px",
              }}
            >
              <ListItemText primary="Booths" />
            </ListItem>
            <ListItem
              button
              onClick={() => handleTabChange("NetworkingSpaces")}
              selected={activeTab === "NetworkingSpaces"}
              sx={{
                borderLeft: "3px solid",
                borderLeftColor:
                  activeTab === "NetworkingSpaces"
                    ? "primary.main"
                    : "transparent",
                backgroundColor:
                  activeTab === "NetworkingSpaces"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                minHeight: "50px",
              }}
            >
              <ListItemText primary="Networking Spaces" />
            </ListItem>
            <ListItem
              button
              onClick={() => handleTabChange("Settings")}
              selected={activeTab === "Settings"}
              sx={{
                borderLeft: "3px solid",
                borderLeftColor:
                  activeTab === "Settings" ? "primary.main" : "transparent",
                backgroundColor:
                  activeTab === "Settings"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                minHeight: "50px",
              }}
            >
              <ListItemText primary="Space Settings" />
            </ListItem>
          </List>
        </Paper>
      </Grid>

      <Grid item xs={12} md={9}>
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          <Alert
            onClose={() => setAlert({ ...alert, open: false })}
            severity={alert.severity}
          >
            {alert.message}
          </Alert>
        </Snackbar>

        {activeTab === "Booths" && (
          <Box>
            {/* Header section */}
            <Box
              mb={4}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" component="h2">
                Virtual Booths
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Booth
              </Button>
            </Box>

            {/* Description section */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="body1" paragraph>
                Create virtual booths for your exhibitors, sponsors, or
                information areas. Each booth can have chat functionality,
                networking tables, and live broadcast capabilities.
              </Typography>
            </Paper>

            {/* Booths grid */}
            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : booths.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No booths created yet
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Create your first booth by clicking the "Add Booth" button
                  above
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Booth
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {booths.map((booth) => (
                  <Grid item xs={12} sm={6} md={4} key={booth.id}>
                    <BoothCard elevation={2}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {booth.boothName}
                        </Typography>

                        {booth.boothExhibitors && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            <strong>Exhibitors:</strong> {booth.boothExhibitors}
                          </Typography>
                        )}

                        {booth.description && (
                          <Typography variant="body2" paragraph>
                            {booth.description}
                          </Typography>
                        )}

                        <Box mt={2} mb={1}>
                          <Typography variant="subtitle2">Features:</Typography>
                          <Box display="flex" flexWrap="wrap" mt={1}>
                            <Tooltip title="Chat enabled">
                              <FeatureChip
                                icon={<ChatIcon />}
                                label="Chat"
                                active={booth.chatEnabled}
                                size="small"
                              />
                            </Tooltip>
                            <Tooltip title="Tables enabled">
                              <FeatureChip
                                icon={<TableIcon />}
                                label={`Tables (${booth.numberOfTables})`}
                                active={booth.tablesEnabled}
                                size="small"
                              />
                            </Tooltip>
                            <Tooltip title="Live broadcast enabled">
                              <FeatureChip
                                icon={<LiveTvIcon />}
                                label="Live"
                                active={booth.liveBroadcastEnabled}
                                size="small"
                              />
                            </Tooltip>
                          </Box>
                        </Box>

                        {booth.boothTags && (
                          <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Tags:</strong> {booth.boothTags}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>

                      <Divider />

                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenDialog(booth)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(booth)}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </BoothCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {activeTab === "NetworkingSpaces" && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Networking Spaces
            </Typography>
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1">
                This feature is coming soon. Networking spaces will allow you to
                create dedicated areas for attendees to connect and interact.
              </Typography>
            </Paper>
          </Box>
        )}

        {activeTab === "Settings" && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Space Settings
            </Typography>
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1">
                Configure global settings for your event spaces. This feature is
                currently in development.
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Booth create/edit dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingBooth
              ? `Edit Booth: ${editingBooth.boothName}`
              : "Create New Booth"}
          </DialogTitle>

          <Formik
            initialValues={editingBooth || initialValues}
            validationSchema={boothValidationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <DialogContent dividers>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        name="boothName"
                        label="Booth Name"
                        fullWidth
                        required
                        value={values.boothName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.boothName && Boolean(errors.boothName)}
                        helperText={touched.boothName && errors.boothName}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="boothExhibitors"
                        label="Exhibitors"
                        fullWidth
                        value={values.boothExhibitors}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.boothExhibitors &&
                          Boolean(errors.boothExhibitors)
                        }
                        helperText={
                          touched.boothExhibitors && errors.boothExhibitors
                        }
                        placeholder="Name of companies/organizations exhibiting at this booth"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="description"
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.description && Boolean(errors.description)
                        }
                        helperText={touched.description && errors.description}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="boothTags"
                        label="Tags"
                        fullWidth
                        value={values.boothTags}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.boothTags && Boolean(errors.boothTags)}
                        helperText={
                          (touched.boothTags && errors.boothTags) ||
                          "Enter comma-separated tags (e.g., Technology, Sponsor, Information)"
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="websiteUrl"
                        label="Website URL"
                        fullWidth
                        value={values.websiteUrl || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.websiteUrl && Boolean(errors.websiteUrl)}
                        helperText={
                          (touched.websiteUrl && errors.websiteUrl) ||
                          "Example: https://example.com"
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Booth Features
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.chatEnabled}
                              onChange={(e) =>
                                setFieldValue("chatEnabled", e.target.checked)
                              }
                              name="chatEnabled"
                              color="primary"
                            />
                          }
                          label="Enable Chat"
                        />

                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.tablesEnabled}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue("tablesEnabled", checked);
                                // If disabling tables, reset number of tables to 0
                                if (!checked) {
                                  setFieldValue("numberOfTables", 0);
                                } else if (values.numberOfTables === 0) {
                                  // If enabling tables and tables are 0, set to 1
                                  setFieldValue("numberOfTables", 1);
                                }
                              }}
                              name="tablesEnabled"
                              color="primary"
                            />
                          }
                          label="Enable Networking Tables"
                        />

                        {values.tablesEnabled && (
                          <Box ml={3} mt={1}>
                            <TextField
                              name="numberOfTables"
                              label="Number of Tables"
                              type="number"
                              value={values.numberOfTables}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.numberOfTables &&
                                Boolean(errors.numberOfTables)
                              }
                              helperText={
                                touched.numberOfTables && errors.numberOfTables
                              }
                              inputProps={{ min: 1, max: 20 }}
                              fullWidth
                            />
                          </Box>
                        )}

                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.liveBroadcastEnabled}
                              onChange={(e) =>
                                setFieldValue(
                                  "liveBroadcastEnabled",
                                  e.target.checked
                                )
                              }
                              name="liveBroadcastEnabled"
                              color="primary"
                            />
                          }
                          label="Enable Live Broadcast"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </DialogContent>

                <DialogActions>
                  <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} />
                    ) : editingBooth ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the booth "
              {boothToDelete?.boothName}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
}

export default EventSpacesComponent;
